import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ClipboardList, Package, Receipt, Plus, Pencil, Trash2, Download, Mail,
  ChevronLeft, ChevronRight, FileText, Loader2, Search, Coins,
  Warehouse, AlertTriangle, EyeOff, Info, Camera
} from "lucide-react";
import EstraiSpesaDaScontrinoDialog from "../components/prima-nota/EstraiSpesaDaScontrinoDialog";
import PrimaNotaKpiBar from "../components/prima-nota/PrimaNotaKpiBar";
import PrimaNotaSectionTabs from "../components/prima-nota/PrimaNotaSectionTabs";
import PrimaNotaListinoTab from "../components/prima-nota/PrimaNotaListinoTab";
import PrimaNotaRicorrenzeTab from "../components/prima-nota/PrimaNotaRicorrenzeTab";
import PrimaNotaReportTab from "../components/prima-nota/PrimaNotaReportTab";
import PrimaNotaMagazzinoTab from "../components/prima-nota/PrimaNotaMagazzinoTab";
import {
  computeKpiFromMagazzino, getSpesaFatturazioneBadge, costoUnitarioConsumo,
  exportCsv, ASL_SCADENZA_GIORNI,
} from "../utils/primaNotaHelpers";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";

import { formatCameraAssegnata } from "../utils/formatCamera";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_AUSILIO = [
  { value: "pannoloni", label: "Pannoloni" },
  { value: "traverse", label: "Traverse" },
];

const TIPI_SPESA = [
  { value: "parrucchiere", label: "Parrucchiere" },
  { value: "podologo", label: "Podologo" },
  { value: "visita_specialistica", label: "Visita Specialistica" },
  { value: "acquisto_ausili", label: "Acquisto Ausili" },
  { value: "altro", label: "Altro" },
];

const MESI = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

function getMeseCorrente() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function formatMese(mese) {
  const [year, month] = mese.split("-");
  return `${MESI[parseInt(month) - 1]} ${year}`;
}

function navigateMese(mese, dir) {
  const [y, m] = mese.split("-").map(Number);
  let nm = m + dir, ny = y;
  if (nm > 12) { nm = 1; ny++; }
  if (nm < 1) { nm = 12; ny--; }
  return `${ny}-${String(nm).padStart(2, "0")}`;
}

function formatData(data) {
  if (!data) return "—";
  try {
    const [y, m, d] = data.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return data;
  }
}

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

function BillingExclusionToggle({ id, checked, onCheckedChange }) {
  return (
    <div className={`rounded-xl border p-3 transition-colors ${
      checked ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"
    }`}>
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={value => onCheckedChange(value === true)}
          className="mt-0.5 h-5 w-5 rounded-md border-amber-300 data-[state=checked]:bg-amber-500 data-[state=checked]:text-white"
        />
        <Label htmlFor={id} className="flex-1 cursor-pointer">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <EyeOff className="h-4 w-4 text-amber-600" />
            Escludi dalla fatturazione
          </span>
          <span className="mt-1 block text-xs leading-5 text-slate-500">
            La voce resta registrata in Prima Nota, ma non verrà proposta nelle fatture.
          </span>
        </Label>
      </div>
    </div>
  );
}

const EMPTY_CONSUMO = {
  ospiteId: "",
  tipoAusilio: "",
  quantita: 1,
  data: todayISO(),
  note: "",
  escludiDaFatturazione: false
};
const EMPTY_SPESA = {
  ospiteId: "",
  tipo: "",
  importo: "",
  descrizione: "",
  data: todayISO(),
  note: "",
  escludiDaFatturazione: false,
  omaggio: false,
};

export default function PrimaNotaOspiti() {
  const { token, activeStruttura } = useAuth();
  const [searchParams] = useSearchParams();
  const [meseCorrente, setMeseCorrente] = useState(getMeseCorrente);
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "magazzino");

  const [ospiti, setOspiti] = useState([]);
  const [consumi, setConsumi] = useState([]);
  const [spese, setSpese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  const [filtroOspite, setFiltroOspite] = useState("tutti");

  const [showAddConsumo, setShowAddConsumo] = useState(false);
  const [showAddSpesa, setShowAddSpesa] = useState(false);
  const [showSpesaAiDialog, setShowSpesaAiDialog] = useState(false);
  const [editingConsumoId, setEditingConsumoId] = useState(null);
  const [editingSpesaId, setEditingSpesaId] = useState(null);
  const [newConsumo, setNewConsumo] = useState(EMPTY_CONSUMO);
  const [newSpesa, setNewSpesa] = useState(EMPTY_SPESA);

  const [reportOspite, setReportOspite] = useState("");

  const [tariffe, setTariffe] = useState([]);
  const [listinoVoci, setListinoVoci] = useState([]);
  const [draftListino, setDraftListino] = useState([]);
  const [savingTariffe, setSavingTariffe] = useState(false);
  const [kpiOperativi, setKpiOperativi] = useState({
    pacchiDaOrdinare: 0, reminderDaInviare: 0, reportDaInviare: 0, fornitureAslInScadenza: 0,
  });
  const [reportInviatiIds, setReportInviatiIds] = useState(new Set());
  const [consumiPage, setConsumiPage] = useState(1);
  const CONSUMI_PER_PAGE = 10;

  const [magazzino, setMagazzino] = useState([]);
  const [loadingMag, setLoadingMag] = useState(false);
  const [showMagConfig, setShowMagConfig] = useState(false);
  const [magConfigOspiteId, setMagConfigOspiteId] = useState("");
  const [magDraft, setMagDraft] = useState({
    pannoloni: { consumoGiornaliero: 0, fornituraAslTrimestrale: 0, dataInizioFornituraAsl: "", fornitore: "parente" },
    traverse: { consumoGiornaliero: 0, fornituraAslTrimestrale: 0, dataInizioFornituraAsl: "", fornitore: "parente" },
  });
  const [savingMag, setSavingMag] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);

  const ospiteMap = useMemo(() => {
    const map = {};
    ospiti.forEach(o => { map[o.id] = o; });
    return map;
  }, [ospiti]);

  const ospitiAttivi = useMemo(() => ospiti.filter(o => !o.archiviata), [ospiti]);

  const filteredConsumi = useMemo(() => {
    if (filtroOspite === "tutti") return consumi;
    return consumi.filter(c => c.ospiteId === filtroOspite);
  }, [consumi, filtroOspite]);

  const filteredSpese = useMemo(() => {
    if (filtroOspite === "tutti") return spese;
    return spese.filter(s => s.ospiteId === filtroOspite);
  }, [spese, filtroOspite]);

  const totals = useMemo(() => ({
    pannoloni: filteredConsumi.filter(c => c.tipoAusilio === "pannoloni").reduce((s, c) => s + (c.quantita || 0), 0),
    traverse: filteredConsumi.filter(c => c.tipoAusilio === "traverse").reduce((s, c) => s + (c.quantita || 0), 0),
    costoAusili: filteredConsumi.reduce((s, c) => {
      const v = c.costoEuro;
      if (v == null || v === "") return s;
      const n = Number(v);
      return s + (Number.isFinite(n) ? n : 0);
    }, 0),
    spese: filteredSpese.reduce((s, x) => s + (x.importo || 0), 0),
  }), [filteredConsumi, filteredSpese]);

  const magMap = useMemo(() => {
    const m = {};
    magazzino.forEach(e => { m[e.ospiteId] = e; });
    return m;
  }, [magazzino]);

  const aslAlerts = useMemo(() => {
    const alerts = [];
    for (const entry of magazzino) {
      for (const tipo of ["pannoloni", "traverse"]) {
        const c = entry[tipo];
        if (!c || (c.consumoGiornaliero || 0) === 0) continue;
        const gg = c.giorniAllaScadenza;
        if (gg == null) continue;
        const ospite = ospiteMap[entry.ospiteId];
        const nome = ospite?.nome_completo || "Ospite";
        if (gg < 0) alerts.push({ ospiteId: entry.ospiteId, nome, tipo, gg, level: "expired" });
        else if (gg <= ASL_SCADENZA_GIORNI) alerts.push({ ospiteId: entry.ospiteId, nome, tipo, gg, level: "warning" });
      }
    }
    alerts.sort((a, b) => a.gg - b.gg);
    return alerts;
  }, [magazzino, ospiteMap]);

  // --- Data fetching ---

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setActiveTab(tab);
    const osp = searchParams.get("ospite");
    if (osp) {
      setFiltroOspite(osp);
      setReportOspite(osp);
    }
  }, [searchParams]);

  useEffect(() => {
    axios.get(`${API_URL}/api/fascicoli-assistenziali`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setOspiti(res.data || []))
      .catch(err => console.error("Errore caricamento ospiti:", err));
  }, [token, activeStruttura?.id]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/prima-nota/listino`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const v = res.data.voci || [];
        setListinoVoci(v);
        setDraftListino(v.map(row => ({ ...row })));
        setTariffe(
          v.filter(x => x.categoria === "ausili").map(x => ({
            tipoAusilio: x.codice, prezzoPacco: x.prezzoUnitario, pezziPerPacco: x.pezziPerPacco || 30,
          }))
        );
      })
      .catch(() => {
        axios.get(`${API_URL}/api/prima-nota/tariffe`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => {
            const t = res.data.tariffe || [];
            setTariffe(t);
          })
          .catch(() => {});
      });
  }, [token, activeStruttura?.id]);

  useEffect(() => {
    if (!token || !meseCorrente) return;
    axios
      .get(`${API_URL}/api/prima-nota/kpi-operativi`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { mese: meseCorrente },
      })
      .then(res => setKpiOperativi(res.data))
      .catch(() => {
        setKpiOperativi(computeKpiFromMagazzino(magazzino, ospitiAttivi, meseCorrente, reportInviatiIds));
      });
  }, [token, meseCorrente, magazzino, ospitiAttivi, reportInviatiIds, activeStruttura?.id]);

  const fetchMagazzino = async () => {
    setLoadingMag(true);
    try {
      const res = await axios.get(`${API_URL}/api/prima-nota/magazzino`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMagazzino(res.data.magazzino || []);
    } catch { /* silent */ }
    finally { setLoadingMag(false); }
  };

  useEffect(() => { fetchMagazzino(); }, [token, activeStruttura?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/prima-nota/consumi`, { headers: h, params: { mese: meseCorrente } }),
      axios.get(`${API_URL}/api/prima-nota/spese`, { headers: h, params: { mese: meseCorrente } }),
    ])
      .then(([cRes, sRes]) => {
        setConsumi(cRes.data.consumi || []);
        setSpese(sRes.data.spese || []);
      })
      .catch(() => toast.error("Errore nel caricamento dei dati"))
      .finally(() => setLoading(false));
  }, [token, meseCorrente, activeStruttura?.id]);

  // --- Handlers ---

  const refreshConsumi = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/prima-nota/consumi`, {
        headers: { Authorization: `Bearer ${token}` }, params: { mese: meseCorrente }
      });
      setConsumi(res.data.consumi || []);
    } catch { /* already toasted */ }
  };

  const refreshSpese = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/prima-nota/spese`, {
        headers: { Authorization: `Bearer ${token}` }, params: { mese: meseCorrente }
      });
      setSpese(res.data.spese || []);
    } catch { /* already toasted */ }
  };

  const fetchListino = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/prima-nota/listino`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const v = res.data.voci || [];
      setListinoVoci(v);
      setDraftListino(v.map(row => ({ ...row })));
      setTariffe(
        v.filter(x => x.categoria === "ausili").map(x => ({
          tipoAusilio: x.codice, prezzoPacco: x.prezzoUnitario, pezziPerPacco: x.pezziPerPacco || 30,
        }))
      );
    } catch {
      toast.error("Errore nel caricamento del listino");
    }
  };

  const handleSaveListino = async () => {
    const voci = [];
    for (const row of draftListino) {
      const codice = (row.codice || row.nome || "").trim().toLowerCase().replace(/\s+/g, "_");
      const prezzo = parseFloat(String(row.prezzoUnitario).replace(",", "."));
      if (!codice || !row.nome?.trim()) {
        toast.error("Ogni voce deve avere nome e codice");
        return;
      }
      if (Number.isNaN(prezzo) || prezzo < 0) {
        toast.error("Prezzo non valido");
        return;
      }
      voci.push({
        id: row.id?.startsWith("tmp-") ? undefined : row.id,
        categoria: row.categoria || "servizi",
        codice,
        nome: row.nome.trim(),
        descrizione: row.descrizione || "",
        prezzoUnitario: prezzo,
        pezziPerPacco: row.categoria === "ausili" ? (row.pezziPerPacco || 30) : null,
        attivo: row.attivo !== false,
      });
    }
    setSavingTariffe(true);
    try {
      await axios.put(
        `${API_URL}/api/prima-nota/listino`,
        { voci },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Listino salvato");
      await fetchListino();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio listino");
    } finally {
      setSavingTariffe(false);
    }
  };

  const closeConsumoDialog = () => {
    setShowAddConsumo(false);
    setEditingConsumoId(null);
    setNewConsumo({ ...EMPTY_CONSUMO, data: todayISO() });
  };

  const closeSpesaDialog = () => {
    setShowAddSpesa(false);
    setEditingSpesaId(null);
    setNewSpesa({ ...EMPTY_SPESA, data: todayISO() });
  };

  const openNewConsumo = () => {
    setEditingConsumoId(null);
    setNewConsumo({ ...EMPTY_CONSUMO, data: todayISO() });
    setShowAddConsumo(true);
  };

  const openNewSpesa = (preset = null) => {
    setEditingSpesaId(null);
    setNewSpesa(preset || { ...EMPTY_SPESA, data: todayISO() });
    setShowAddSpesa(true);
  };

  const applySpesaFromAi = (fields) => {
    setEditingSpesaId(null);
    setNewSpesa((prev) => ({
      ...EMPTY_SPESA,
      ...prev,
      ospiteId: fields.ospiteId || prev.ospiteId || "",
      tipo: fields.tipo || prev.tipo || "",
      importo: fields.importo != null && fields.importo !== "" ? String(fields.importo) : prev.importo || "",
      descrizione: fields.descrizione || prev.descrizione || "",
      data: fields.data || prev.data || todayISO(),
      note: fields.note || prev.note || "",
    }));
    setShowAddSpesa(true);
    toast.success("Dati dallo scontrino applicati — verifica e salva");
  };

  const openEditConsumo = (c) => {
    setEditingConsumoId(c.id);
    setNewConsumo({
      ospiteId: c.ospiteId || "",
      tipoAusilio: c.tipoAusilio || "",
      quantita: c.quantita ?? 1,
      data: (c.data || "").slice(0, 10) || todayISO(),
      note: c.note || "",
      escludiDaFatturazione: !!c.escludiDaFatturazione,
    });
    setShowAddConsumo(true);
  };

  const openEditSpesa = (s) => {
    setEditingSpesaId(s.id);
    setNewSpesa({
      ospiteId: s.ospiteId || "",
      tipo: s.tipo || "",
      importo: s.importo != null ? String(s.importo) : "",
      descrizione: s.descrizione || "",
      data: (s.data || "").slice(0, 10) || todayISO(),
      note: s.note || "",
      escludiDaFatturazione: !!s.escludiDaFatturazione,
      omaggio: !!s.omaggio,
    });
    setShowAddSpesa(true);
  };

  const handleSaveConsumo = async () => {
    if (!newConsumo.ospiteId) return toast.error("Seleziona un ospite");
    if (!newConsumo.tipoAusilio) return toast.error("Seleziona il tipo di ausilio");
    if (!newConsumo.quantita || parseInt(newConsumo.quantita, 10) < 1) return toast.error("Inserisci una quantità valida (pezzi singoli)");
    if (!newConsumo.data) return toast.error("Seleziona la data");

    const payload = {
      ospiteId: newConsumo.ospiteId,
      tipoAusilio: newConsumo.tipoAusilio,
      quantita: parseInt(newConsumo.quantita, 10),
      data: newConsumo.data,
      note: newConsumo.note || null,
      escludiDaFatturazione: !!newConsumo.escludiDaFatturazione,
    };

    setSaving(true);
    try {
      if (editingConsumoId) {
        const res = await axios.put(
          `${API_URL}/api/prima-nota/consumi/${editingConsumoId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const updated = res.data?.consumo;
        if (updated?.id) {
          setConsumi(prev => prev.map(c => (c.id === updated.id ? { ...c, ...updated } : c)));
        } else {
          await refreshConsumi();
        }
        toast.success("Consumo aggiornato");
      } else {
        await axios.post(`${API_URL}/api/prima-nota/consumi`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Consumo registrato");
        await refreshConsumi();
      }
      closeConsumoDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSpesa = async () => {
    if (!newSpesa.ospiteId) return toast.error("Seleziona un ospite");
    if (!newSpesa.tipo) return toast.error("Seleziona il tipo di spesa");
    if (!newSpesa.importo || parseFloat(newSpesa.importo) <= 0) return toast.error("Inserisci un importo valido");
    if (!newSpesa.data) return toast.error("Seleziona la data");

    const payload = {
      ospiteId: newSpesa.ospiteId,
      tipo: newSpesa.tipo,
      importo: parseFloat(newSpesa.importo),
      descrizione: newSpesa.descrizione || null,
      data: newSpesa.data,
      note: newSpesa.note || null,
      escludiDaFatturazione: !!newSpesa.escludiDaFatturazione,
      omaggio: !!newSpesa.omaggio,
    };

    setSaving(true);
    try {
      if (editingSpesaId) {
        const res = await axios.put(
          `${API_URL}/api/prima-nota/spese/${editingSpesaId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const updated = res.data?.spesa;
        if (updated?.id) {
          setSpese(prev => prev.map(s => (s.id === updated.id ? { ...s, ...updated } : s)));
        } else {
          await refreshSpese();
        }
        toast.success("Spesa aggiornata");
      } else {
        await axios.post(`${API_URL}/api/prima-nota/spese`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Spesa registrata");
        await refreshSpese();
      }
      closeSpesaDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConsumo = async (id) => {
    if (!window.confirm("Eliminare questo consumo?")) return;
    try {
      await axios.delete(`${API_URL}/api/prima-nota/consumi/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Consumo eliminato");
      setConsumi(prev => prev.filter(c => c.id !== id));
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDeleteSpesa = async (id) => {
    if (!window.confirm("Eliminare questa spesa?")) return;
    try {
      await axios.delete(`${API_URL}/api/prima-nota/spese/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Spesa eliminata");
      setSpese(prev => prev.filter(s => s.id !== id));
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleGenerateReport = async (inviaEmail = false) => {
    if (!reportOspite) return toast.error("Seleziona un ospite");
    setGeneratingReport(true);
    try {
      if (inviaEmail) {
        const res = await axios.post(`${API_URL}/api/prima-nota/report`, {
          ospiteId: reportOspite, mese: meseCorrente, inviaEmail: true
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(res.data.message || "Report inviato ai familiari");
      } else {
        const res = await axios.post(`${API_URL}/api/prima-nota/report`, {
          ospiteId: reportOspite, mese: meseCorrente, inviaEmail: false
        }, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        const nome = ospiteMap[reportOspite]?.nome_completo?.replace(/\s+/g, "_") || "ospite";
        a.download = `report_${meseCorrente}_${nome}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Report scaricato");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella generazione del report");
    } finally {
      setGeneratingReport(false);
    }
  };

  const openMagConfig = (ospiteId) => {
    const existing = magMap[ospiteId];
    const emptyAusilio = { consumoGiornaliero: 0, fornituraAslTrimestrale: 0, dataInizioFornituraAsl: "", fornitore: "parente" };
    if (existing) {
      const toForm = (c) => ({
        consumoGiornaliero: c.consumoGiornaliero ?? 0,
        fornituraAslTrimestrale: c.fornituraAslTrimestrale ?? 0,
        dataInizioFornituraAsl: c.dataInizioFornituraAsl || "",
        fornitore: c.fornitore || "parente",
      });
      setMagDraft({ pannoloni: toForm(existing.pannoloni || {}), traverse: toForm(existing.traverse || {}) });
    } else {
      setMagDraft({ pannoloni: { ...emptyAusilio }, traverse: { ...emptyAusilio } });
    }
    setMagConfigOspiteId(ospiteId);
    setShowMagConfig(true);
  };

  const handleSaveMagConfig = async () => {
    setSavingMag(true);
    try {
      const payload = {
        pannoloni: {
          consumoGiornaliero: parseInt(magDraft.pannoloni.consumoGiornaliero, 10) || 0,
          fornituraAslTrimestrale: parseInt(magDraft.pannoloni.fornituraAslTrimestrale, 10) || 0,
          dataInizioFornituraAsl: magDraft.pannoloni.dataInizioFornituraAsl || null,
          fornitore: magDraft.pannoloni.fornitore || "parente",
        },
        traverse: {
          consumoGiornaliero: parseInt(magDraft.traverse.consumoGiornaliero, 10) || 0,
          fornituraAslTrimestrale: parseInt(magDraft.traverse.fornituraAslTrimestrale, 10) || 0,
          dataInizioFornituraAsl: magDraft.traverse.dataInizioFornituraAsl || null,
          fornitore: magDraft.traverse.fornitore || "parente",
        },
      };
      await axios.put(
        `${API_URL}/api/prima-nota/magazzino/${magConfigOspiteId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Configurazione magazzino salvata");
      setShowMagConfig(false);
      await fetchMagazzino();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingMag(false);
    }
  };

  const handleSendReminder = async (ospiteId) => {
    setSendingReminder(ospiteId);
    try {
      const res = await axios.post(
        `${API_URL}/api/prima-nota/magazzino/${ospiteId}/reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Reminder inviato");
      await fetchMagazzino();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio del reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const handleRegisterSpesaFromMag = (ospiteId) => {
    const entry = magMap[ospiteId];
    if (!entry) return;
    let descrizione = "";
    for (const tipo of ["pannoloni", "traverse"]) {
      const c = entry[tipo];
      if (c && c.pacchiDaOrdinare > 0) {
        if (descrizione) descrizione += " + ";
        descrizione += `${c.pacchiDaOrdinare} pacchi ${tipo}`;
      }
    }
    openNewSpesa({
      ospiteId,
      tipo: "acquisto_ausili",
      importo: "",
      descrizione: `Acquisto ausili: ${descrizione}`,
      data: todayISO(),
      note: "",
      escludiDaFatturazione: false,
      omaggio: false,
      _lockTipo: true,
    });
  };

  const getTipoAusilioLabel = v => TIPI_AUSILIO.find(t => t.value === v)?.label || v;
  const getTipoSpesaLabel = v => TIPI_SPESA.find(t => t.value === v)?.label || v;

  const formatCostoCell = (c) => {
    const v = c.costoEuro;
    if (v == null || v === "") return "—";
    const n = Number(v);
    return Number.isFinite(n) ? `€ ${n.toFixed(2)}` : "—";
  };

  const costoConsumiOspiteMese = (ospiteId) =>
    consumi
      .filter(c => c.ospiteId === ospiteId)
      .reduce((s, c) => {
        const v = c.costoEuro;
        if (v == null || v === "") return s;
        const n = Number(v);
        return s + (Number.isFinite(n) ? n : 0);
      }, 0);

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="pl-4 pt-4">
        <PageHeader
          title="Prima Nota Ospiti"
          subtitle="Tracciamento consumi ausili e spese extra"
          icon={ClipboardList}
          iconContainerClassName="bg-gradient-to-r from-rose-500 to-rose-600"
          iconClassName="text-white"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Navigazione mese + Filtro ospite */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-1 bg-white rounded-xl shadow-sm px-3 py-2">
            <Button variant="ghost" size="sm" onClick={() => setMeseCorrente(m => navigateMese(m, -1))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-semibold text-slate-700 min-w-[160px] text-center">
              {formatMese(meseCorrente)}
            </span>
            <Button variant="ghost" size="sm" onClick={() => setMeseCorrente(m => navigateMese(m, 1))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <Select value={filtroOspite} onValueChange={setFiltroOspite}>
              <SelectTrigger className="w-[220px] bg-white">
                <SelectValue placeholder="Filtra per ospite" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti gli ospiti</SelectItem>
                {ospitiAttivi.map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.nome_completo}{" "}
                    {o.camera
                      ? `(${formatCameraAssegnata(o.camera) || o.camera})`
                      : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <PrimaNotaKpiBar kpi={kpiOperativi} onNavigate={setActiveTab} />

        <p className="text-xs text-blue-800 mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
          <Info className="w-3.5 h-3.5 text-blue-500 shrink-0" />
          I dati si aggiornano automaticamente in base alla sezione selezionata e al mese corrente.
        </p>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <PrimaNotaSectionTabs />

          <TabsContent value="magazzino">
            <PrimaNotaMagazzinoTab
              ospitiAttivi={ospitiAttivi}
              magMap={magMap}
              loadingMag={loadingMag}
              aslAlerts={aslAlerts}
              sendingReminder={sendingReminder}
              onSendReminder={handleSendReminder}
              onOpenMagConfig={openMagConfig}
              onRegisterSpesaFromMag={handleRegisterSpesaFromMag}
              onOpenReport={(ospiteId) => {
                setReportOspite(ospiteId);
                setActiveTab("report");
              }}
            />
          </TabsContent>

          {/* ========== CONSUMI EXTRA TAB ========== */}
          <TabsContent value="consumi">
            <Card className="border-0 shadow-lg rounded-2xl bg-white">
              <CardHeader className="border-b bg-slate-50 rounded-t-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-500" />
                    Consumi Extra — {formatMese(meseCorrente)}
                  </CardTitle>
                  <p className="text-sm text-slate-600 mt-1">
                    Registra i consumi di ausili extra fornitura da addebitare all&apos;ospite.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => exportCsv(
                    `consumi_extra_${meseCorrente}.csv`,
                    ["Data", "Ospite", "Camera", "Ausilio", "Pezzi", "Costo unit.", "Costo totale", "Registrato da"],
                    filteredConsumi.map(c => [
                      formatData(c.data),
                      ospiteMap[c.ospiteId]?.nome_completo,
                      formatCameraAssegnata(ospiteMap[c.ospiteId]?.camera) || "",
                      getTipoAusilioLabel(c.tipoAusilio),
                      c.quantita,
                      costoUnitarioConsumo(c)?.toFixed(2),
                      c.costoEuro,
                      c.registratoDaNome,
                    ]),
                  )}>
                    <Download className="w-4 h-4 mr-1" /> Esporta Excel
                  </Button>
                  <Button onClick={openNewConsumo} className="bg-rose-500 hover:bg-rose-600">
                    <Plus className="w-4 h-4 mr-1.5" /> Registra consumo
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
                  </div>
                ) : filteredConsumi.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">Nessun consumo registrato</p>
                    <p className="text-sm mt-1">Clicca "Registra" per inserire il primo</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="font-semibold text-slate-700">Data</TableHead>
                          <TableHead className="font-semibold text-slate-700">Ospite</TableHead>
                          <TableHead className="font-semibold text-slate-700">Camera</TableHead>
                          <TableHead className="font-semibold text-slate-700">Ausilio</TableHead>
                          <TableHead className="font-semibold text-slate-700 text-center">Pezzi</TableHead>
                          <TableHead className="font-semibold text-slate-700 text-right">Costo unit.</TableHead>
                          <TableHead className="font-semibold text-slate-700 text-right">Costo totale</TableHead>
                          <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">Registrato da</TableHead>
                          <TableHead className="font-semibold text-slate-700 text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredConsumi
                          .slice((consumiPage - 1) * CONSUMI_PER_PAGE, consumiPage * CONSUMI_PER_PAGE)
                          .map(c => (
                          <TableRow key={c.id} className="hover:bg-slate-50">
                            <TableCell className="font-medium">{formatData(c.data)}</TableCell>
                            <TableCell className="font-medium text-slate-900">
                              {ospiteMap[c.ospiteId]?.nome_completo || "—"}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatCameraAssegnata(ospiteMap[c.ospiteId]?.camera) || "—"}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                c.tipoAusilio === "pannoloni"
                                  ? "border-violet-200 text-violet-700 bg-violet-50"
                                  : "border-green-200 text-green-700 bg-green-50"
                              }>
                                {getTipoAusilioLabel(c.tipoAusilio)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center font-semibold">{c.quantita}</TableCell>
                            <TableCell className="text-right text-sm text-slate-600 whitespace-nowrap">
                              {(() => {
                                const u = costoUnitarioConsumo(c);
                                return u != null ? `€ ${u.toFixed(2)}` : "—";
                              })()}
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-amber-700 whitespace-nowrap">
                              {formatCostoCell(c)}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                              {c.registratoDaNome || "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-0.5">
                                <Button variant="ghost" size="sm" onClick={() => openEditConsumo(c)}
                                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                  title="Modifica">
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteConsumo(c.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title="Elimina">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {filteredConsumi.length > CONSUMI_PER_PAGE && (
                      <div className="flex items-center justify-between px-6 py-3 border-t text-sm text-slate-600">
                        <span>
                          {(consumiPage - 1) * CONSUMI_PER_PAGE + 1} - {Math.min(consumiPage * CONSUMI_PER_PAGE, filteredConsumi.length)} di {filteredConsumi.length}
                        </span>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={consumiPage <= 1} onClick={() => setConsumiPage(p => p - 1)}>Precedente</Button>
                          <Button variant="outline" size="sm" disabled={consumiPage * CONSUMI_PER_PAGE >= filteredConsumi.length} onClick={() => setConsumiPage(p => p + 1)}>Successiva</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-slate-500 px-6 py-3 border-t bg-blue-50/50">
                  I consumi vengono calcolati in base al listino corrente. Ultimo aggiornamento: {formatData(todayISO())}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="listino">
            <PrimaNotaListinoTab
              voci={draftListino}
              onChange={setDraftListino}
              onSave={handleSaveListino}
              saving={savingTariffe}
            />
          </TabsContent>

          <TabsContent value="ricorrenze">
            <PrimaNotaRicorrenzeTab
              token={token}
              meseCorrente={meseCorrente}
              ospitiAttivi={ospitiAttivi}
              listinoVoci={listinoVoci}
              filtroOspite={filtroOspite}
            />
          </TabsContent>

          {/* ========== SPESE TAB ========== */}
          <TabsContent value="spese">
            <Card className="border-0 shadow-lg rounded-2xl bg-white">
              <CardHeader className="border-b bg-slate-50 rounded-t-2xl flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-rose-500" />
                  Spese Extra — {formatMese(meseCorrente)}
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => exportCsv(
                    `spese_extra_${meseCorrente}.csv`,
                    ["Data", "Ospite", "Tipo", "Descrizione", "Importo", "Fatturazione", "Note"],
                    filteredSpese.map(s => {
                      const b = getSpesaFatturazioneBadge(s);
                      return [formatData(s.data), ospiteMap[s.ospiteId]?.nome_completo, getTipoSpesaLabel(s.tipo), s.descrizione, s.importo, b.label, s.note];
                    }),
                  )}>
                    <Download className="w-4 h-4 mr-1" /> Esporta Excel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                    onClick={() => {
                      openNewSpesa();
                      setShowSpesaAiDialog(true);
                    }}
                  >
                    <Camera className="w-4 h-4 mr-1" /> Da scontrino
                  </Button>
                  <Button onClick={() => openNewSpesa()} className="bg-rose-500 hover:bg-rose-600">
                    <Plus className="w-4 h-4 mr-1.5" /> Registra spesa
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
                  </div>
                ) : filteredSpese.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">Nessuna spesa registrata</p>
                    <p className="text-sm mt-1">Clicca "Registra" per inserire la prima</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="font-semibold text-slate-700">Data</TableHead>
                          <TableHead className="font-semibold text-slate-700">Ospite</TableHead>
                          <TableHead className="font-semibold text-slate-700">Tipo</TableHead>
                          <TableHead className="font-semibold text-slate-700 hidden sm:table-cell">Descrizione</TableHead>
                          <TableHead className="font-semibold text-slate-700 text-right">Importo</TableHead>
                          <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">Fatturazione</TableHead>
                          <TableHead className="font-semibold text-slate-700 hidden md:table-cell">Note</TableHead>
                          <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">Registrato da</TableHead>
                          <TableHead className="font-semibold text-slate-700 text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSpese.map(s => (
                          <TableRow key={s.id} className="hover:bg-slate-50">
                            <TableCell className="font-medium">{formatData(s.data)}</TableCell>
                            <TableCell>
                              <span className="font-medium text-slate-900">
                                {ospiteMap[s.ospiteId]?.nome_completo || "—"}
                              </span>
                              {ospiteMap[s.ospiteId]?.camera && (
                                <span className="text-xs text-slate-400 ml-1">
                                  (
                                  {formatCameraAssegnata(ospiteMap[s.ospiteId].camera) ||
                                    ospiteMap[s.ospiteId].camera}
                                  )
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="border-rose-200 text-rose-700 bg-rose-50">
                                {getTipoSpesaLabel(s.tipo)}
                              </Badge>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell text-sm text-slate-600 max-w-[200px] truncate">
                              {s.descrizione || "—"}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-rose-600">
                              € {(s.importo || 0).toFixed(2)}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {(() => {
                                const b = getSpesaFatturazioneBadge(s);
                                return <Badge variant="outline" className={b.className}>{b.label}</Badge>;
                              })()}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-sm text-slate-500 max-w-[150px] truncate">
                              {s.note || "—"}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                              {s.registratoDaNome || "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-0.5">
                                <Button variant="ghost" size="sm" onClick={() => openEditSpesa(s)}
                                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                  title="Modifica">
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteSpesa(s.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title="Elimina">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="report">
            <PrimaNotaReportTab
              token={token}
              meseCorrente={meseCorrente}
              formatMese={formatMese}
              ospitiAttivi={ospitiAttivi}
              reportOspite={reportOspite}
              setReportOspite={setReportOspite}
              filtroOspite={filtroOspite}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* ========== DIALOG: AGGIUNGI CONSUMO ========== */}
      <Dialog open={showAddConsumo} onOpenChange={open => { if (!open) closeConsumoDialog(); else setShowAddConsumo(true); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              {editingConsumoId ? "Modifica consumo ausilio" : "Registra consumo ausilio"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Ospite *</Label>
              <Select value={newConsumo.ospiteId} onValueChange={v => setNewConsumo(p => ({ ...p, ospiteId: v }))}>
                <SelectTrigger><SelectValue placeholder="Seleziona ospite..." /></SelectTrigger>
                <SelectContent>
                  {ospitiAttivi.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.nome_completo}{" "}
                    {o.camera
                      ? `(${formatCameraAssegnata(o.camera) || o.camera})`
                      : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo Ausilio *</Label>
              <Select
                value={newConsumo.tipoAusilio}
                onValueChange={v => setNewConsumo(p => ({ ...p, tipoAusilio: v }))}
              >
                <SelectTrigger><SelectValue placeholder="Seleziona tipo..." /></SelectTrigger>
                <SelectContent>
                  {TIPI_AUSILIO.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Quantità (pezzi singoli consumati) *</Label>
              <Input type="number" min="1" value={newConsumo.quantita}
                onChange={e => setNewConsumo(p => ({ ...p, quantita: e.target.value }))} />
              <p className="text-xs text-slate-500">Indica i singoli pezzi utilizzati, non il numero di pacchi.</p>
            </div>
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input type="date" value={newConsumo.data}
                onChange={e => setNewConsumo(p => ({ ...p, data: e.target.value }))} />
            </div>
            <BillingExclusionToggle
              id="consumo-escludi-fatturazione"
              checked={!!newConsumo.escludiDaFatturazione}
              onCheckedChange={value => setNewConsumo(p => ({ ...p, escludiDaFatturazione: value }))}
            />
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea placeholder="Note opzionali..." value={newConsumo.note} rows={2}
                onChange={e => setNewConsumo(p => ({ ...p, note: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeConsumoDialog}>Annulla</Button>
            <Button onClick={handleSaveConsumo} disabled={saving} className="bg-rose-500 hover:bg-rose-600">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : editingConsumoId ? <Pencil className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              {editingConsumoId ? "Salva modifiche" : "Registra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DIALOG: AGGIUNGI / MODIFICA SPESA ========== */}
      <Dialog open={showAddSpesa} onOpenChange={open => { if (!open) closeSpesaDialog(); else setShowAddSpesa(true); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-rose-500" />
              {editingSpesaId ? "Modifica spesa extra" : "Registra spesa extra"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editingSpesaId ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-rose-200 text-rose-700 hover:bg-rose-50 gap-2"
                onClick={() => setShowSpesaAiDialog(true)}
              >
                <Camera className="w-4 h-4" />
                Fotografa scontrino (IA)
              </Button>
            ) : null}
            <div className="space-y-2">
              <Label>Ospite *</Label>
              <Select value={newSpesa.ospiteId} onValueChange={v => setNewSpesa(p => ({ ...p, ospiteId: v }))}>
                <SelectTrigger><SelectValue placeholder="Seleziona ospite..." /></SelectTrigger>
                <SelectContent>
                  {ospitiAttivi.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.nome_completo}{" "}
                    {o.camera
                      ? `(${formatCameraAssegnata(o.camera) || o.camera})`
                      : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo Spesa *</Label>
              <Select value={newSpesa.tipo} onValueChange={v => setNewSpesa(p => ({ ...p, tipo: v }))} disabled={!!newSpesa._lockTipo}>
                <SelectTrigger><SelectValue placeholder="Seleziona tipo..." /></SelectTrigger>
                <SelectContent>
                  {TIPI_SPESA.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Importo (€) *</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={newSpesa.importo}
                onChange={e => setNewSpesa(p => ({ ...p, importo: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input placeholder="Descrizione opzionale..." value={newSpesa.descrizione}
                onChange={e => setNewSpesa(p => ({ ...p, descrizione: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input type="date" value={newSpesa.data}
                onChange={e => setNewSpesa(p => ({ ...p, data: e.target.value }))} />
            </div>
            <BillingExclusionToggle
              id="spesa-escludi-fatturazione"
              checked={!!newSpesa.escludiDaFatturazione}
              onCheckedChange={value => setNewSpesa(p => ({ ...p, escludiDaFatturazione: value }))}
            />
            <div className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50/40 p-3">
              <Checkbox
                id="spesa-omaggio"
                checked={!!newSpesa.omaggio}
                onCheckedChange={value => setNewSpesa(p => ({ ...p, omaggio: !!value }))}
              />
              <Label htmlFor="spesa-omaggio" className="cursor-pointer font-normal leading-snug">
                <span className="font-medium text-slate-800">Omaggio</span>
                <span className="mt-1 block text-xs text-slate-500">
                  La spesa resta in Prima Nota ma non entra in fatturazione.
                </span>
              </Label>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea placeholder="Note opzionali..." value={newSpesa.note} rows={2}
                onChange={e => setNewSpesa(p => ({ ...p, note: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeSpesaDialog}>Annulla</Button>
            <Button onClick={handleSaveSpesa} disabled={saving} className="bg-rose-500 hover:bg-rose-600">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : editingSpesaId ? <Pencil className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              {editingSpesaId ? "Salva modifiche" : "Registra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EstraiSpesaDaScontrinoDialog
        open={showSpesaAiDialog}
        onOpenChange={setShowSpesaAiDialog}
        token={token}
        apiPath="/api/prima-nota/spese/extract-scontrino"
        ospiti={ospitiAttivi}
        onApply={applySpesaFromAi}
      />

      {/* ========== DIALOG: CONFIGURAZIONE MAGAZZINO ========== */}
      <Dialog open={showMagConfig} onOpenChange={setShowMagConfig}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-rose-500" />
              Configurazione Magazzino — {ospiteMap[magConfigOspiteId]?.nome_completo || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {["pannoloni", "traverse"].map(tipo => {
              const label = tipo === "pannoloni" ? "Pannoloni" : "Traverse";
              const draft = magDraft[tipo];
              const updateField = (field, value) =>
                setMagDraft(prev => ({ ...prev, [tipo]: { ...prev[tipo], [field]: value } }));
              return (
                <div key={tipo} className={`rounded-xl border p-4 space-y-3 ${
                  tipo === "pannoloni" ? "bg-blue-50/40 border-blue-100" : "bg-green-50/40 border-green-100"
                }`}>
                  <h4 className="font-semibold text-sm">{label}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Consumo giornaliero (pz)</Label>
                      <Input type="number" min={0} max={30} value={draft.consumoGiornaliero}
                        onChange={e => updateField("consumoGiornaliero", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Fornitura ASL trimestrale (pz)</Label>
                      <Input type="number" min={0} value={draft.fornituraAslTrimestrale}
                        onChange={e => updateField("fornituraAslTrimestrale", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Data inizio fornitura ASL</Label>
                      <Input type="date" value={draft.dataInizioFornituraAsl}
                        onChange={e => updateField("dataInizioFornituraAsl", e.target.value)} />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Chi fornisce gli ausili?</Label>
                      <Select value={draft.fornitore} onValueChange={v => updateField("fornitore", v)}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="parente">Parente (reminder email)</SelectItem>
                          <SelectItem value="struttura">Struttura (fattura)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMagConfig(false)}>Annulla</Button>
            <Button onClick={handleSaveMagConfig} disabled={savingMag} className="bg-rose-500 hover:bg-rose-600">
              {savingMag ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Salva configurazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
